import { create } from 'zustand'
import type {
  BlobStorageRequest, LogicAppsRequest, FunctionsRequest,
  AppServiceRequest, BandwidthRequest, ChatParsedQuery, VirtualDesktopRequest
} from '../api/types'

// Default values for each calculator
const defaultBlob: BlobStorageRequest = {
  storageTB: 1,
  tier: 'hot',
  redundancy: 'LRS',
  writeOpsPerMonth: 10000,
  readOpsPerMonth: 100000,
  listOpsPerMonth: 1000,
  gbRetrievedPerMonth: 0,
  enablePITR: false,
  gbToRestoreIfPITR: 0,
  changeOpsPerMonth: 0,
  daysStored: 31,
}

const defaultLogicApps: LogicAppsRequest = {
  planType: 'consumption',
  runsPerDay: 100,
  actionsPerRun: 5,
  standardConnectorCallsPerMonth: 0,
  enterpriseConnectorCallsPerMonth: 0,
}

const defaultFunctions: FunctionsRequest = {
  planType: 'consumption',
  executionsPerMonth: 1000000,
  avgDurationMs: 200,
  avgMemoryMB: 128,
}

const defaultAppService: AppServiceRequest = {
  tier: 'premium_v3',
  os: 'Linux',
  sku: 'P1v3',
  instanceCount: 1,
  autoscaleEnabled: false,
}

const defaultBandwidth: BandwidthRequest = {
  outboundInternetGB: 0,
  interRegionGB: 0,
}

const defaultVirtualDesktop: VirtualDesktopRequest = {
  sessionHosts: {
    vmSku: 'D4s v3',
    instanceCount: 5,
    hoursPerMonth: 730,
    azureHybridBenefit: false,
    hostingModel: 'multi_session',
  },
  fslogix: {
    storageTier: 'standard',
    provisionedGB: 500,
    estimatedTransactionsPerUserPerDay: 1000,
  },
  users: {
    internalCount: 50,
    internalHasEligibleM365: true,
    externalCount: 0,
    externalAccessType: 'apps_only',
  },
  networking: {
    outboundInternetGB: 0,
    natGatewayEnabled: false,
    natGatewayHoursPerMonth: 744,
    natGatewayDataProcessedGB: 0,
  },
}

type VirtualDesktopInputsPatch = {
  sessionHosts?: Partial<VirtualDesktopRequest['sessionHosts']>
  fslogix?: Partial<VirtualDesktopRequest['fslogix']>
  users?: Partial<VirtualDesktopRequest['users']>
  networking?: Partial<VirtualDesktopRequest['networking']>
}

interface CalculatorStore {
  // Global discount modifier (EA / CSP / MACC) — persists across calculators
  discount: number
  setDiscount: (value: number) => void

  // Form inputs
  blobInputs: BlobStorageRequest
  logicAppsInputs: LogicAppsRequest
  functionsInputs: FunctionsRequest
  appServiceInputs: AppServiceRequest
  bandwidthInputs: BandwidthRequest
  virtualDesktopInputs: VirtualDesktopRequest

  // Setters (partial update — merges with existing state)
  setBlobInputs: (inputs: Partial<BlobStorageRequest>) => void
  setLogicAppsInputs: (inputs: Partial<LogicAppsRequest>) => void
  setFunctionsInputs: (inputs: Partial<FunctionsRequest>) => void
  setAppServiceInputs: (inputs: Partial<AppServiceRequest>) => void
  setBandwidthInputs: (inputs: Partial<BandwidthRequest>) => void
  setVirtualDesktopInputs: (inputs: VirtualDesktopInputsPatch) => void

  // Chat auto-fill — populates forms from Worker parsed_query
  applyParsedQuery: (query: ChatParsedQuery) => void

  // AI adjustment history (Sprint 23) — session-only, not persisted
  adjustmentHistory: string[]
  addAdjustment: (label: string) => void
  resetAdjustmentHistory: () => void

  // Reset a single calculator to defaults
  resetBlob: () => void
  resetLogicApps: () => void
  resetFunctions: () => void
  resetAppService: () => void
  resetBandwidth: () => void
  resetVirtualDesktop: () => void
}

export const useCalculatorStore = create<CalculatorStore>((set) => ({
  discount: 0,
  setDiscount: (value) => set({ discount: Math.min(70, Math.max(0, value)) }),

  adjustmentHistory: [],
  addAdjustment: (label) => set((s) => ({
    adjustmentHistory: [...s.adjustmentHistory, label].slice(-10),
  })),
  resetAdjustmentHistory: () => set({ adjustmentHistory: [] }),

  blobInputs: defaultBlob,
  logicAppsInputs: defaultLogicApps,
  functionsInputs: defaultFunctions,
  appServiceInputs: defaultAppService,
  bandwidthInputs: defaultBandwidth,
  virtualDesktopInputs: defaultVirtualDesktop,

  setBlobInputs: (inputs) =>
    set((s) => ({ blobInputs: { ...s.blobInputs, ...inputs } })),
  setLogicAppsInputs: (inputs) =>
    set((s) => ({ logicAppsInputs: { ...s.logicAppsInputs, ...inputs } })),
  setFunctionsInputs: (inputs) =>
    set((s) => ({ functionsInputs: { ...s.functionsInputs, ...inputs } })),
  setAppServiceInputs: (inputs) =>
    set((s) => ({ appServiceInputs: { ...s.appServiceInputs, ...inputs } })),
  setBandwidthInputs: (inputs) =>
    set((s) => ({ bandwidthInputs: { ...s.bandwidthInputs, ...inputs } })),
  setVirtualDesktopInputs: (inputs: VirtualDesktopInputsPatch) =>
    set((s) => ({
      virtualDesktopInputs: {
        ...s.virtualDesktopInputs,
        sessionHosts: {
          ...s.virtualDesktopInputs.sessionHosts,
          ...(inputs.sessionHosts ?? {}),
        },
        fslogix: {
          ...s.virtualDesktopInputs.fslogix,
          ...(inputs.fslogix ?? {}),
        },
        users: {
          ...s.virtualDesktopInputs.users,
          ...(inputs.users ?? {}),
        },
        networking: {
          ...s.virtualDesktopInputs.networking,
          ...(inputs.networking ?? {}),
        },
      },
    })),

  applyParsedQuery: (query) =>
    set((s) => ({
      blobInputs: query.blob
        ? { ...s.blobInputs, ...query.blob }
        : s.blobInputs,
      logicAppsInputs: query.logicApps
        ? { ...s.logicAppsInputs, ...query.logicApps }
        : s.logicAppsInputs,
      functionsInputs: query.functions
        ? { ...s.functionsInputs, ...query.functions }
        : s.functionsInputs,
      appServiceInputs: query.appService
        ? { ...s.appServiceInputs, ...query.appService }
        : s.appServiceInputs,
      bandwidthInputs: query.bandwidth
        ? { ...s.bandwidthInputs, ...query.bandwidth }
        : s.bandwidthInputs,
    })),

  resetBlob: () => set({ blobInputs: defaultBlob }),
  resetLogicApps: () => set({ logicAppsInputs: defaultLogicApps }),
  resetFunctions: () => set({ functionsInputs: defaultFunctions }),
  resetAppService: () => set({ appServiceInputs: defaultAppService }),
  resetBandwidth: () => set({ bandwidthInputs: defaultBandwidth }),
  resetVirtualDesktop: () => set({ virtualDesktopInputs: defaultVirtualDesktop }),
}))
